/**
 * The arithmetic of the `countdown` section, apart from React so it can be
 * read (and tested) on its own.
 */

/** How far ahead a theme's static preview sets its demo clock. */
export const DEMO_COUNTDOWN_MS = 2 * 24 * 60 * 60 * 1000;

export interface Remaining {
  /** Milliseconds left, never below zero. */
  total: number;
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

/**
 * What is left of the clock at `now`. With `showDays` off the days fold into
 * the hours - "51:03:04" rather than a clock that silently drops two days.
 */
export function remaining(endsAt: number, now: number, showDays = true): Remaining {
  const total = Math.max(0, endsAt - now);
  // Rounded up, so the clock reads 00:00:01 through the last second and only
  // shows zero once time is truly up - and a tick that lands a few
  // milliseconds late never skips a digit.
  const totalSeconds = Math.ceil(total / 1000);
  const days = Math.floor(totalSeconds / 86_400);
  const hours = Math.floor((totalSeconds % 86_400) / 3600);
  return {
    total,
    days: showDays ? days : 0,
    hours: showDays ? hours : days * 24 + hours,
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

/** The instant an ISO string names, or null for a blank or broken one. */
export function parseEndsAt(value: string | null | undefined): number | null {
  if (!value) return null;
  const time = Date.parse(value);
  return Number.isNaN(time) ? null : time;
}

/** Two digits at least, so a ticking clock never changes width. */
export function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * The offset of `zone` from UTC at one instant, in minutes. Read from `Intl`
 * rather than a table, so a store in a zone with daylight saving gets the
 * right hour on either side of the change.
 */
function zoneOffsetMinutes(zone: string, at: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(at));
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((each) => each.type === type)?.value ?? 0);
  const asUtc = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour') % 24,
    part('minute'),
    part('second'),
  );
  return Math.round((asUtc - Math.floor(at / 1000) * 1000) / 60_000);
}

function safeZone(zone: string | null | undefined): string {
  if (!zone) return 'Asia/Kolkata';
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return zone;
  } catch {
    return 'Asia/Kolkata';
  }
}

/**
 * `2026-10-20` and `23:59`, read as wall-clock time in the store's zone, as a
 * UTC ISO string. Null for anything that is not a real date and time.
 */
export function zonedToUtcIso(date: string, time: string, zone?: string | null): string | null {
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date.trim());
  const clock = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!day || !clock) return null;
  const [year, month, dayOfMonth] = [Number(day[1]), Number(day[2]), Number(day[3])];
  const [hours, minutes] = [Number(clock[1]), Number(clock[2])];
  if (month < 1 || month > 12 || hours > 23 || minutes > 59) return null;
  const wall = Date.UTC(year, month - 1, dayOfMonth, hours, minutes);
  if (new Date(wall).getUTCDate() !== dayOfMonth) return null;
  const tz = safeZone(zone);
  // Twice: the first guess's offset can be the wrong side of a DST change.
  let instant = wall - zoneOffsetMinutes(tz, wall) * 60_000;
  instant = wall - zoneOffsetMinutes(tz, instant) * 60_000;
  return new Date(instant).toISOString();
}

/** The store-zone date and time of an instant, as the customiser's fields hold them. */
export function utcToZonedFields(iso: string, zone?: string | null): { date: string; time: string } {
  const at = Date.parse(iso);
  if (Number.isNaN(at)) return { date: '', time: '' };
  const shifted = new Date(at + zoneOffsetMinutes(safeZone(zone), at) * 60_000);
  return {
    date: `${shifted.getUTCFullYear()}-${pad(shifted.getUTCMonth() + 1)}-${pad(shifted.getUTCDate())}`,
    time: `${pad(shifted.getUTCHours())}:${pad(shifted.getUTCMinutes())}`,
  };
}
